import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useUploadFileToGallery } from '@/app/(main)/gallery/hooks/useUploadFileToGallery';
import { CLIENT_ID_QUERY_KEY } from '@/consts/const';
import { useIsClientProfileForm } from '@/context/ClientProfileFormProvider';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import { useUploadFileData } from '@/hooks/useUploadFileData';
import { getConvertedToWebpImages } from '@/utils/getConvertedToWebpImages';
import { useEditClientMutation } from '../../../hooks/useEditClientMutation';
import { useDeleteClientProfileImageMutation } from '../useDeleteClientProfileImageMutation';

export const useUploadImageToClient = () => {
  const [query] = useSearchQuery();
  const clientId = query.get(CLIENT_ID_QUERY_KEY);
  const { uploadFileData, isLoading: isUploadFileDataLoading } = useUploadFileToGallery();
  const { uploadFileData: uploadFileToStorage, isLoading: isUploadFileToStorageLoading } = useUploadFileData();
  const isClientProfileForm = useIsClientProfileForm();
  const { mutateAsync: editClient, isPending: isEditClientPending } = useEditClientMutation();
  const { mutate: deleteClientProfileImage, isPending: isDeleteClientProfileImagePending } =
    useDeleteClientProfileImageMutation();

  const isLoading = isUploadFileDataLoading || isUploadFileToStorageLoading || isEditClientPending;

  const onUploadImageFromGallery = useCallback(
    async (imageId: number, onSuccess?: () => void) => {
      const response = await editClient(
        {
          data: {
            client: {
              profile_picture_gallery_id: imageId,
            },
          },
          clientId: Number(clientId),
        },
        {
          onSuccess: () => {
            onSuccess?.();
          },
        }
      );

      return {
        profile_picture_url: response.data.client.profile_picture_url,
      };
    },
    [editClient, clientId]
  );

  const onUploadImage = useCallback(
    async (file: File, onSuccess?: () => void) => {
      if (isClientProfileForm) {
        const { storage_id } = await uploadFileToStorage(file);
        const response = await editClient(
          {
            data: {
              client: {
                profile_picture_storage_id: storage_id,
              },
            },
            clientId: Number(clientId),
          },
          {
            onSuccess: () => {
              onSuccess?.();
            },
          }
        );
        return {
          profile_picture_url: response.data.client.profile_picture_url,
          profile_picture_gallery_id: null,
        };
      }

      const convertedImages = await getConvertedToWebpImages([file]);
      const uploadFileDataResult = await uploadFileData(convertedImages[0]);
      const response = await editClient(
        {
          data: {
            client: {
              profile_picture_gallery_id: uploadFileDataResult.gallery_id,
            },
          },
          clientId: Number(clientId),
        },
        {
          onSuccess: () => {
            onSuccess?.();
          },
        }
      );
      return {
        profile_picture_url: response.data.client.profile_picture_url,
        profile_picture_gallery_id: uploadFileDataResult.gallery_id,
      };
    },
    [isClientProfileForm, uploadFileToStorage, uploadFileData, editClient, clientId]
  );

  const onDeleteImage = useCallback(
    (clientId: number, onSuccess?: () => void) => {
      deleteClientProfileImage(
        {
          clientId,
        },
        {
          onSuccess: () => {
            toast.success('Image deleted successfully');
            onSuccess?.();
          },
          onError: () => {
            toast.error('Error deleting image');
          },
        }
      );
    },
    [deleteClientProfileImage]
  );

  return {
    onUploadImage,
    onDeleteImage,
    onUploadImageFromGallery,
    isLoading,
    isDeleteLoading: isDeleteClientProfileImagePending,
  };
};
